import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useId, useState } from "react";
import { FaBirthdayCake } from "react-icons/fa";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { FloatingInput } from "@/components/ui/floating-input";
import { FloatingSelect } from "@/components/ui/floating-select";
import { useAuth } from "@/hooks/use-auth";
import { api, edenFetch } from "@/lib/eden";
import { AUTH_ME_QUERY_KEY, queryClient } from "@/lib/queryClient";
import { detectTimezone } from "@shared/account-data";

import { readClaimMarker, startAccountClaim } from "./account-claim";
import { MONTH_OPTIONS, YEAR_OPTIONS, dayOptionsFor } from "./auth-utils";
import { FormErrorMessage } from "./form-error";

const UNDER_13 = "You need to be at least 13 years old to become a member of birthdayalarm.com";

/** BDAY-452: an older account with the same name + birthday (server `SignupTwin`). */
type Twin = { email_hint: string | null; sign_in_with: string[]; member_since: number | null };

const METHOD_LABEL: Record<string, string> = {
  email: "email and password",
  google: "Google",
  apple: "Apple",
  facebook: "Facebook",
};

function signInWords(methods: string[]): string {
  const labels = methods.map((m) => METHOD_LABEL[m] ?? m);
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}

/**
 * The social sign-up "Almost there!" step (PRD 08 D19) — port of legacy
 * `components/auth/details`: name, the Month/Day/Year birthday row, keep-my-age-secret.
 *
 * Legacy showed it before a Google/Apple/Facebook member existed; our popup has to
 * mint the session first, so the server stamps the new account and `/user/auth/me`
 * reports `signup_details_required` until this is submitted. Mounted at the root so it
 * holds the member on every route, chrome-free ones included. Not dismissable — the
 * only other way out is signing out.
 *
 * The 13-year cutoff is deliberately NOT pre-checked here: the server closes an
 * under-13 account (anonymizes it) and answers `under_13`, which is the point — a
 * client-side block would leave the account open and the member stuck.
 */
export function SignupDetailsGate() {
  const { user, logout } = useAuth();
  const flags = user as unknown as {
    signup_details_required?: boolean;
    impersonation?: unknown;
    display_name?: string | null;
    email?: string | null;
  } | null;
  // An admin signed in AS the member must not be trapped by (or answer) this.
  const required = !!flags?.signup_details_required && !flags?.impersonation;

  if (!required) return null;
  return (
    <SignupDetailsDialog
      initialName={flags?.display_name ?? ""}
      email={flags?.email ?? null}
      onSignOut={logout}
    />
  );
}

function SignupDetailsDialog({
  initialName,
  email,
  onSignOut,
}: {
  initialName: string;
  email: string | null;
  onSignOut: () => Promise<void>;
}) {
  // Apple "Hide My Email" addresses never match an existing account — the commonest
  // way a long-time member lands here with an empty new account (PRD 08 D21).
  const isHiddenEmail = !!email && /@privaterelay\.appleid\.com$/i.test(email);
  // They chose "I already have an account" and then signed in to THIS new one again.
  const [cameBackHere] = useState(() => readClaimMarker() === "adopt_new");
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");
  const claimExisting = async () => {
    setClaiming(true);
    setClaimError("");
    try {
      await startAccountClaim("adopt_new"); // navigates to /login
    } catch (e) {
      setClaimError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
      setClaiming(false);
    }
  };
  const cakeLabelId = useId();
  const [name, setName] = useState(initialName);
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  const [keepAgeSecret, setKeepAgeSecret] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState("");
  const [closed, setClosed] = useState(false);
  // BDAY-452: set when the server found an older account with this name + birthday.
  const [twins, setTwins] = useState<Twin[] | null>(null);

  const missing = {
    name: !name.trim() ? "Name is required!" : undefined,
    month: !month ? "Month required!" : undefined,
    day: !day ? "Day required!" : undefined,
    year: !year ? "Year required!" : undefined,
  };
  const valid = !missing.name && !missing.month && !missing.day && !missing.year;

  const submit = useMutation({
    mutationFn: (opts: { confirmNew: boolean }) =>
      edenFetch(
        api.api.user.me["signup-details"].post({
          birthday: `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
          is_age_hidden: keepAgeSecret,
          display_name: name.trim(),
          timezone: detectTimezone(),
          check_existing: true,
          confirm_new: opts.confirmNew,
        }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY }),
    onError: (e: unknown) => {
      const value = (e as { value?: { detail?: string; code?: string; matches?: Twin[] } })?.value;
      if (value?.code === "possible_existing" && value.matches?.length) {
        setTwins(value.matches);
        return;
      }
      const detail =
        value?.detail ??
        (e as { message?: string })?.message ??
        "We couldn't save your details. Please try again.";
      if (detail === UNDER_13) {
        // The server closed the account and dropped the session.
        setClosed(true);
        void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
        return;
      }
      setError(detail);
    },
  });

  if (closed) {
    return (
      <Dialog open>
        <DialogContent showCloseButton={false} className="max-w-[440px] text-center">
          <DialogTitle className="font-serif text-[1.75rem] font-light">Sorry!</DialogTitle>
          <DialogDescription className="text-[16px] text-foreground">{UNDER_13}</DialogDescription>
          <Button className="mt-4" onClick={() => (window.location.href = "/")}>
            OK
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  if (twins) {
    return (
      <Dialog open>
        <DialogContent showCloseButton={false} className="max-w-[440px]" data-testid="signup-twin">
          <DialogTitle className="text-center font-serif text-[1.75rem] font-light">
            Is this you?
          </DialogTitle>
          <DialogDescription className="text-center text-[16px] text-foreground">
            We found {twins.length === 1 ? "a BirthdayAlarm account" : "BirthdayAlarm accounts"}{" "}
            with the same name and birthday. If {twins.length === 1 ? "it's" : "one is"} yours, sign
            in to it and we'll connect this sign-in to it, so you keep your reminders, cards and
            membership.
          </DialogDescription>
          <ul className="mt-2 flex flex-col gap-2">
            {twins.map((t, i) => (
              <li
                key={i}
                className="rounded-md border border-primary/40 bg-primary/5 p-3 text-[15px]"
              >
                {t.email_hint && <div className="font-semibold">{t.email_hint}</div>}
                {t.sign_in_with.length > 0 && (
                  <div>Signs in with {signInWords(t.sign_in_with)}</div>
                )}
                {t.member_since && (
                  <div className="text-foreground/70">Member since {t.member_since}</div>
                )}
              </li>
            ))}
          </ul>
          <Button
            type="button"
            size="cta"
            className="mt-3 w-full"
            disabled={claiming || submit.isPending}
            onClick={() => void claimExisting()}
            data-testid="button-twin-sign-in"
          >
            {claiming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Yes, sign in to that account
          </Button>
          {claimError && <FormErrorMessage>{claimError}</FormErrorMessage>}
          <Button
            type="button"
            variant="outline"
            className="mt-2 w-full"
            disabled={claiming || submit.isPending}
            onClick={() => submit.mutate({ confirmNew: true })}
            data-testid="button-twin-not-me"
          >
            {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            No, that's not me. Create a new account
          </Button>
          {error && <FormErrorMessage>{error}</FormErrorMessage>}
          <button
            type="button"
            className="mt-2 cursor-pointer text-sm text-foreground/70 hover:underline"
            disabled={submit.isPending}
            onClick={() => {
              setTwins(null);
              setError("");
            }}
          >
            Back
          </button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open>
      <DialogContent showCloseButton={false} className="max-w-[440px]">
        <DialogTitle className="text-center font-serif text-[1.75rem] font-light">
          Almost there!
        </DialogTitle>
        <div className="mt-1 rounded-md border border-primary/40 bg-primary/5 p-3 text-[15px]">
          {cameBackHere ? (
            <p>
              <b>That was your new account again.</b> To reach the account you already have, sign in
              with the email and password, Google or Facebook you used before — not this one.
            </p>
          ) : (
            <p>
              <b>Already have a BirthdayAlarm account?</b>{" "}
              {isHiddenEmail
                ? "You signed in with Apple's Hide My Email, so we couldn't match you to it. "
                : "If this sign-in didn't find it, "}
              sign in to that account instead and we'll connect this sign-in to it.
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            className="mt-3 w-full"
            disabled={claiming}
            onClick={() => void claimExisting()}
          >
            {claiming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Yes, sign in to my existing account
          </Button>
          {claimError && <FormErrorMessage>{claimError}</FormErrorMessage>}
        </div>
        <DialogDescription className="mt-3 text-center text-[16px] text-foreground">
          New to BirthdayAlarm? Review your information and make sure everything looks good.
        </DialogDescription>
        <form
          className="mt-2 flex flex-col"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            setTouched(true);
            setError("");
            if (valid) submit.mutate({ confirmNew: false });
          }}
        >
          <FloatingInput
            label="Full name"
            id="details-name"
            value={name}
            maxLength={50}
            onChange={(e) => setName(e.target.value)}
            error={touched ? missing.name : undefined}
          />
          <div id={cakeLabelId} className="mt-4 mb-2 flex items-center font-semibold">
            <FaBirthdayCake className="mt-[-3px] mr-2.5 text-primary" aria-hidden />
            <span>Birthday</span>
          </div>
          <div className="flex w-full gap-2.5" role="group" aria-labelledby={cakeLabelId}>
            <div className="min-w-[calc(46%-20px)] flex-1">
              <FloatingSelect
                label="Month"
                id="details-month"
                name="month"
                required
                items={MONTH_OPTIONS}
                value={month || null}
                onValueChange={(v) => {
                  setMonth(v);
                  if (day && Number(day) > dayOptionsFor(v, year).length) setDay("");
                }}
                error={touched ? missing.month : undefined}
              />
            </div>
            <div className="min-w-[26%] flex-1">
              <FloatingSelect
                label="Day"
                id="details-day"
                name="day"
                required
                items={dayOptionsFor(month, year)}
                value={day || null}
                onValueChange={setDay}
                error={touched ? missing.day : undefined}
              />
            </div>
            <div className="min-w-[28%] flex-1">
              <FloatingSelect
                label="Year"
                id="details-year"
                name="year"
                required
                items={YEAR_OPTIONS}
                value={year || null}
                onValueChange={(v) => {
                  setYear(v);
                  if (day && Number(day) > dayOptionsFor(month, v).length) setDay("");
                }}
                error={touched ? missing.year : undefined}
              />
            </div>
          </div>
          <label className="mt-[15px] mb-[15px] flex cursor-pointer items-center gap-2.5 font-normal">
            <Checkbox
              id="details-keepAgeSecret"
              checked={keepAgeSecret}
              onCheckedChange={(c) => setKeepAgeSecret(c === true)}
            />
            <span>Keep my age secret</span>
          </label>
          {error && <FormErrorMessage>{error}</FormErrorMessage>}
          <Button type="submit" size="cta" disabled={submit.isPending}>
            {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
          <button
            type="button"
            className="mt-4 cursor-pointer text-sm text-foreground/70 hover:underline"
            onClick={() => void onSignOut()}
          >
            Sign out
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
